import jsPDF from 'jspdf';
import autoTable, { UserOptions } from 'jspdf-autotable';
import logo from '../images/logo.png';
import { actionStatusLabels, reasonLabel } from '../components/epi/epiColors';
import { EpiComplianceRowDTO, EpiComplianceSummaryDTO, EpiInspectionListRowDTO } from '../types/EpiComplianceDTO';
import { EpiCorrectiveActionDTO } from '../types/EpiCorrectiveActionDTO';
import { EpiDossierDTO } from '../types/EpiDossierDTO';
import { IMMEDIATE_ACTIONS } from '../pages/epi-inspection-wizard/steps/EpiChecklistStep';
import { EpiInspectionService } from '../services/EpiInspectionService';

const statusLabels: Record<string, string> = {
	CONFORME: 'Conforme',
	NAO_CONFORME: 'Não conforme',
	SEM_INSPECAO: 'Sem inspeção',
	OK: 'Conforme',
	NAO_OK: 'Não conforme',
	NAO_APLICAVEL: 'Não aplicável',
	ABERTA: 'Aberta',
	EM_ANDAMENTO: 'Em andamento',
	CONCLUIDA: 'Concluída'
};

const MARGIN_X = 15;
const CONTENT_TOP = 40;
const BOTTOM_MARGIN = 15;
const BODY_SIZE = 10;
const TITLE_SIZE = 12;
const HEADER_SIZE = 16;
const TABLE_SIZE = 8;
const LABEL_WIDTH = 55;
const EVIDENCE_MAX_WIDTH = 70;
const EVIDENCE_MAX_HEIGHT = 90;
const SIGNATURE_MAX_WIDTH = 80;
const SIGNATURE_MAX_HEIGHT = 30;
const LOGO_TOP = 10;
const LOGO_MAX_WIDTH = 40;
const LOGO_MAX_HEIGHT = 20;

interface ImageBox {
	width: number;
	height: number;
}

interface HeaderLogo {
	image: string;
	format: string;
	box: ImageBox;
}

export interface EpiInspectionsReportFilters {
	companyName: string;
	period: string;
	ordering: string;
}

export interface EpiNonConformityReportFilters {
	companyName: string;
	situation: string;
	period: string;
	sector: string;
	reason: string;
	responsible: string;
	search: string;
}

interface TextOptions {
	size?: number;
	bold?: boolean;
	x?: number;
	width?: number;
}

class PdfWriter {
	readonly doc: jsPDF;
	y = CONTENT_TOP;
	private readonly title: string;
	private logo: HeaderLogo;

	constructor(title: string, orientation: 'portrait' | 'landscape', companyLogo?: string) {
		this.doc = new jsPDF({ orientation });
		this.title = title;
		this.logo = resolveHeaderLogo(this.doc, companyLogo);
		this.header();
	}

	get pageWidth() {
		return this.doc.internal.pageSize.getWidth();
	}

	get contentWidth() {
		return this.pageWidth - MARGIN_X * 2;
	}

	header() {
		const size = this.doc.getFontSize();
		const style = this.doc.getFont().fontStyle;
		try {
			this.drawLogo();
		} catch {
			this.logo = resolveHeaderLogo(this.doc);
			this.drawLogo();
		}
		this.doc.setFont('helvetica', 'normal');
		this.doc.setFontSize(HEADER_SIZE);
		this.doc.text(this.title, this.pageWidth / 2, 20, { align: 'center' });
		this.doc.setFontSize(size);
		this.doc.setFont('helvetica', style);
	}

	drawLogo() {
		const { image, format, box } = this.logo;
		this.doc.addImage(image, format, MARGIN_X, LOGO_TOP + (LOGO_MAX_HEIGHT - box.height) / 2, box.width, box.height);
	}

	lineHeight(size: number) {
		return size * this.doc.getLineHeightFactor() / (this.doc.internal as any).scaleFactor;
	}

	ensure(height: number) {
		const limit = this.doc.internal.pageSize.getHeight() - BOTTOM_MARGIN;
		if (this.y + height > limit && this.y > CONTENT_TOP) {
			this.doc.addPage();
			this.header();
			this.y = CONTENT_TOP;
		}
	}

	gap(height: number) {
		this.y += height;
	}

	setFont(size: number, bold: boolean) {
		this.doc.setFontSize(size);
		this.doc.setFont('helvetica', bold ? 'bold' : 'normal');
	}

	split(content: string, size: number, bold: boolean, width: number): string[] {
		this.setFont(size, bold);
		return this.doc.splitTextToSize(content, width);
	}

	textHeight(content: string, options: TextOptions = {}) {
		const size = options.size || BODY_SIZE;
		const lines = this.split(content, size, !!options.bold, options.width || this.contentWidth);
		return lines.length * this.lineHeight(size);
	}

	text(content: string, options: TextOptions = {}) {
		const size = options.size || BODY_SIZE;
		const bold = !!options.bold;
		const lines = this.split(content, size, bold, options.width || this.contentWidth);
		const height = this.lineHeight(size);
		lines.forEach(line => {
			this.ensure(height);
			this.setFont(size, bold);
			this.doc.text(line, options.x || MARGIN_X, this.y, { baseline: 'top' });
			this.y += height;
		});
	}

	field(label: string, value?: string) {
		const valueX = MARGIN_X + LABEL_WIDTH + 5;
		const labelLines = this.split(`${label}:`, BODY_SIZE, true, LABEL_WIDTH);
		const valueLines = this.split(value || 'Não informado', BODY_SIZE, false, this.pageWidth - MARGIN_X - valueX);
		const height = this.lineHeight(BODY_SIZE);
		const rows = Math.max(labelLines.length, valueLines.length);
		for (let row = 0; row < rows; row++) {
			this.ensure(height);
			if (labelLines[row]) {
				this.setFont(BODY_SIZE, true);
				this.doc.text(labelLines[row], MARGIN_X, this.y, { baseline: 'top' });
			}
			if (valueLines[row]) {
				this.setFont(BODY_SIZE, false);
				this.doc.text(valueLines[row], valueX, this.y, { baseline: 'top' });
			}
			this.y += height;
		}
		this.gap(2);
	}

	image(image: string, box: ImageBox) {
		this.ensure(box.height);
		try {
			this.doc.addImage(image, imageFormatFromDataUrl(image), MARGIN_X, this.y, box.width, box.height);
		} catch {
			return false;
		}
		this.y += box.height;
		return true;
	}

	table(head: string[][], body: string[][], columnStyles?: UserOptions['columnStyles']) {
		this.ensure(this.lineHeight(TABLE_SIZE) * 4);
		autoTable(this.doc, {
			head,
			body,
			startY: this.y,
			margin: { top: CONTENT_TOP, left: MARGIN_X, right: MARGIN_X, bottom: BOTTOM_MARGIN },
			theme: 'grid',
			styles: { fontSize: TABLE_SIZE, overflow: 'linebreak' },
			headStyles: { fillColor: [33, 150, 243] },
			columnStyles,
			didDrawPage: data => {
				if (data.pageNumber > 1) {
					this.header();
				}
			}
		});
		this.y = (this.doc as any).lastAutoTable.finalY;
	}

	save(fileName: string) {
		this.doc.save(fileName);
	}
}

export class EpiPdfService {
	public exportInspectionsToPdf(rows: EpiInspectionListRowDTO[], filters: EpiInspectionsReportFilters,
			companyLogo?: string): void {
		const writer = new PdfWriter('Inspeções de EPIs realizadas', 'landscape', companyLogo);
		writer.text(`Empresa: ${filters.companyName}`);
		writer.text(`Emitido em: ${new Date().toLocaleDateString('pt-BR')}`);
		writer.text(`Período filtrado: ${filters.period || 'não informado'}`);
		writer.text(`Ordenação: ${filters.ordering}`);
		writer.gap(4);
		writer.table([['Nº', 'Data', 'Colaborador', 'Matrícula', 'Setor', 'Cargo',
			'Conformes', 'Não conformes', 'Não aplicáveis', 'Resultado']],
			rows.map(row => [
				row.number || '',
				row.inspectionDate || '',
				row.employeeName || '',
				row.registration || '',
				row.sector || '',
				row.position || '',
				String(row.okItems),
				String(row.nonConformItems),
				String(row.notApplicableItems),
				row.status === 'CANCELADA' ? 'Cancelada' : (statusLabels[row.result] || '-')
			]));
		writer.save(`inspecoes_epis_${filters.companyName}.pdf`);
	}

	public exportComplianceToPdf(summary: EpiComplianceSummaryDTO, rows: EpiComplianceRowDTO[], companyLogo?: string): void {
		const writer = new PdfWriter('Gestão da Conformidade NR-06', 'landscape', companyLogo);
		writer.text(`Empresa: ${summary.companyName}`);
		writer.text(`Período: ${summary.periodFrom} a ${summary.periodTo}`);
		writer.text(`Emitido em: ${new Date().toLocaleDateString('pt-BR')}`);
		writer.text(`Monitorados: ${summary.monitoredEmployees}  |  Conformes: ${summary.compliantEmployees}`
			+ `  |  Não conformes: ${summary.nonCompliantEmployees}`
			+ `  |  Sem inspeção: ${summary.uninspectedEmployees}`
			+ `  |  Índice: ${formatIndex(summary.complianceIndex)} (meta ${summary.complianceGoal}%)`);
		writer.gap(4);
		writer.table([['Colaborador', 'Matrícula', 'Cargo', 'Setor', 'Índice', 'Última inspeção',
			'NC abertas', 'Ações em andamento', 'Exames', 'Situação']],
			rows.map(row => [
				row.employeeName || '',
				row.registration || '',
				row.position || '',
				row.sector || '',
				formatIndex(row.complianceIndex),
				row.lastInspectionDate || '-',
				String(row.openNonConformities),
				String(row.ongoingActions),
				String(row.pendingExams),
				statusLabels[row.status] || row.status
			]));
		writer.save(`conformidade_nr06_${summary.companyName}.pdf`);
	}

	public exportNonConformitiesToPdf(actions: EpiCorrectiveActionDTO[], filters: EpiNonConformityReportFilters,
			companyLogo?: string): void {
		const writer = new PdfWriter('Não Conformidades e Plano de Ação', 'landscape', companyLogo);
		const countByStatus = (status: string) => actions.filter(action => action.status === status).length;
		writer.text(`Empresa: ${filters.companyName}`);
		writer.text(`Emitido em: ${new Date().toLocaleDateString('pt-BR')}`);
		writer.text(`Filtros: situação ${filters.situation}  |  período ${filters.period}  |  setor ${filters.sector}`
			+ `  |  tipo de problema ${filters.reason}  |  responsável ${filters.responsible}`
			+ (filters.search ? `  |  busca "${filters.search}"` : ''));
		writer.text(`Total: ${actions.length}  |  Abertas: ${countByStatus('ABERTA')}`
			+ `  |  Em andamento: ${countByStatus('EM_ANDAMENTO')}`
			+ `  |  Vencidas: ${actions.filter(action => action.overdue).length}`
			+ `  |  Concluídas: ${countByStatus('CONCLUIDA')}`);
		writer.gap(4);
		writer.table([['Inspeção', 'Colaborador / Setor', 'EPI', 'Motivo', 'Descrição do problema',
			'Ação imediata', 'Ação corretiva', 'Responsável', 'Prazo', 'Situação']],
			actions.map(action => [
				`${action.inspectionNumber || '-'}\n${action.inspectionDate || ''}`,
				`${action.employeeName || '-'}\nMatrícula ${action.employeeRegistration || '-'}`
					+ `${action.sectorName ? `\n${action.sectorName}` : ''}`,
				`${action.epiName || '-'}${action.ca ? `\nCA ${action.ca}` : ''}`,
				action.nonConformityReason
					? reasonLabel(action.nonConformityReason, action.nonConformityReasonDetail)
					: '-',
				action.problemDescription || '-',
				(action.immediateAction && (IMMEDIATE_ACTIONS[action.immediateAction] || action.immediateAction)) || '-',
				action.correctiveAction || '-',
				action.responsibleName || '-',
				action.dueDate
					? `${action.dueDate}${action.daysToDueDate !== undefined && action.daysToDueDate !== null
						? `\n(${action.daysToDueDate} dias)` : ''}`
					: '-',
				action.overdue ? 'Vencida' : actionStatusLabels[action.status] || action.status
			]),
			{
				0: { cellWidth: 18 },
				1: { cellWidth: 34 },
				2: { cellWidth: 28 },
				3: { cellWidth: 24 },
				5: { cellWidth: 24 },
				6: { cellWidth: 36 },
				7: { cellWidth: 24 },
				8: { cellWidth: 20 },
				9: { cellWidth: 19 }
			});
		writer.save(`nao_conformidades_${filters.companyName}.pdf`);
	}

	public exportDossierToPdf(dossier: EpiDossierDTO, images: Record<string, string>, companyLogo?: string): void {
		const writer = new PdfWriter('Evidência de Conformidade NR-06', 'portrait', companyLogo);
		const inspection = dossier.inspection;
		const bodyLine = writer.lineHeight(BODY_SIZE);
		writer.text(`Inspeção nº ${inspection.number || '-'}`, { size: TITLE_SIZE, bold: true });
		writer.gap(3);
		writer.field('Empresa', inspection.companyName);
		writer.field('Colaborador', inspection.employeeName);
		writer.field('Matrícula', inspection.employeeRegistration);
		writer.field('Setor', inspection.sectorName);
		writer.field('Cargo', inspection.positionName);
		writer.field('Data da inspeção', inspection.inspectionDate);
		writer.field('Próxima inspeção', inspection.nextInspectionDate);
		writer.field('Responsável', inspection.responsibleName);
		writer.field('Resultado', statusLabels[inspection.result] || '-');
		writer.field('Observação geral', inspection.generalObservation);
		writer.gap(4);
		writer.table([['EPI', 'CA', 'Última entrega', 'Status', 'Detalhamento']],
			inspection.items.map(item => [
				item.epiName || '',
				item.ca || '-',
				item.lastDeliveryDate || '-',
				statusLabels[item.status || ''] || '-',
				item.observation
					|| (item.immediateAction && (IMMEDIATE_ACTIONS[item.immediateAction] || item.immediateAction))
					|| '-'
			]));
		writer.gap(8);
		inspection.items.forEach(item => {
			const itemImages = (item.evidenceFiles || '')
				.split(',')
				.map(name => images[name.trim()])
				.filter((image): image is string => !!image)
				.map(image => ({ image, box: computeImageBox(writer.doc, image, EVIDENCE_MAX_WIDTH, EVIDENCE_MAX_HEIGHT) }))
				.filter((entry): entry is { image: string, box: ImageBox } => entry.box !== null);
			if (!itemImages.length) {
				return;
			}
			const title = `Evidências: ${item.epiName || '-'}`;
			writer.ensure(writer.textHeight(title, { bold: true }) + 2 + itemImages[0].box.height);
			writer.text(title, { bold: true });
			writer.gap(2);
			itemImages.forEach(({ image, box }) => {
				if (writer.image(image, box)) {
					writer.gap(5);
				}
			});
			writer.gap(3);
		});
		if (dossier.acknowledgementText) {
			writer.ensure(writer.textHeight('Ciência do trabalhador', { bold: true }) + 2 + bodyLine * 2);
			writer.text('Ciência do trabalhador', { bold: true });
			writer.gap(2);
			writer.text(dossier.acknowledgementText);
			writer.gap(2);
			writer.text(`Registrada em: ${dossier.acknowledgedAt || '-'}`);
			writer.gap(8);
		}
		if (dossier.examAcknowledgementText) {
			const title = 'Ciência das pendências de exames';
			const exams = parseExamSnapshot(dossier.examPendingSnapshot);
			writer.ensure(writer.textHeight(title, { bold: true }) + 2
				+ (exams.length ? writer.lineHeight(TABLE_SIZE) * 4 : bodyLine * 2));
			writer.text(title, { bold: true });
			writer.gap(2);
			if (exams.length) {
				writer.table([['Exame', 'Situação', 'Vencimento / prazo']], exams,
					{ 1: { cellWidth: 30 }, 2: { cellWidth: 32 } });
				writer.gap(4);
			}
			writer.text(dossier.examAcknowledgementText);
			writer.gap(2);
			writer.text(`Registrada em: ${dossier.examAcknowledgedAt || '-'}`);
			writer.gap(8);
		}
		dossier.signatures.forEach(signature => {
			const title = signature.role === 'TRABALHADOR' ? 'Assinatura do trabalhador' : 'Assinatura do responsável';
			const caption = `${signature.signerName || '-'} · coletada em ${signature.signedAt}`;
			const image = images[signature.fileName];
			const box = image ? computeImageBox(writer.doc, image, SIGNATURE_MAX_WIDTH, SIGNATURE_MAX_HEIGHT) : null;
			writer.ensure(writer.textHeight(title, { bold: true }) + 2 + (box ? box.height + 3 : 0)
				+ writer.textHeight(caption));
			writer.text(title, { bold: true });
			writer.gap(2);
			if (image && box && writer.image(image, box)) {
				writer.gap(3);
			}
			writer.text(caption);
			writer.gap(8);
		});
		writer.save(`inspecao_${inspection.number || inspection.id}.pdf`);
	}
}

function parseExamSnapshot(snapshot?: string): string[][] {
	return (snapshot || '')
		.split('\n')
		.filter(line => !!line.trim())
		.map(line => {
			const parts = line.split(' | ');
			const dueDate = parts.length > 2 ? parts.pop() : undefined;
			const status = parts.length > 1 ? parts.pop() : undefined;
			return [parts.join(' | '), status, dueDate].map(snapshotValue);
		});
}

function snapshotValue(value?: string) {
	const text = (value || '').trim();
	return !text || text === 'undefined' || text === 'null' ? '-' : text;
}

function formatIndex(value?: number) {
	return value === undefined || value === null ? '-' : `${value.toFixed(1)}%`;
}

function imageFormatFromDataUrl(dataUrl: string) {
	const match = /^data:image\/([a-zA-Z0-9+.-]+);base64,/.exec(dataUrl);
	if (!match) {
		return 'JPEG';
	}
	const subtype = match[1].toUpperCase();
	return subtype === 'JPG' ? 'JPEG' : subtype;
}

function resolveHeaderLogo(doc: jsPDF, companyLogo?: string): HeaderLogo {
	const companyBox = companyLogo ? computeImageBox(doc, companyLogo, LOGO_MAX_WIDTH, LOGO_MAX_HEIGHT) : null;
	if (companyLogo && companyBox) {
		return { image: companyLogo, format: imageFormatFromDataUrl(companyLogo), box: companyBox };
	}
	return {
		image: logo,
		format: 'PNG',
		box: computeImageBox(doc, logo, LOGO_MAX_WIDTH, LOGO_MAX_HEIGHT) || { width: LOGO_MAX_WIDTH, height: LOGO_MAX_HEIGHT }
	};
}

function computeImageBox(doc: jsPDF, image: string, maxWidth: number, maxHeight: number): ImageBox | null {
	try {
		const properties = doc.getImageProperties(image);
		const ratio = properties.width / properties.height;
		if (!ratio || !Number.isFinite(ratio)) {
			return null;
		}
		let width = maxWidth;
		let height = width / ratio;
		if (height > maxHeight) {
			height = maxHeight;
			width = height * ratio;
		}
		return { width, height };
	} catch {
		return null;
	}
}

export async function blobToDataUrl(blob: Blob) {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

export async function loadCompanyLogo(companyId?: number | '') {
	if (!companyId) {
		return undefined;
	}
	const response = await new EpiInspectionService().getLogo(Number(companyId));
	if (response?.status !== 200 || !response.data?.size) {
		return undefined;
	}
	try {
		return await blobToDataUrl(response.data);
	} catch {
		return undefined;
	}
}
